const BASE = process.env.REACT_APP_API_URL || '/api';

export function authHeaders() {
  let t = null;
  try { t = localStorage.getItem('pf_token'); } catch (e) {}
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Download a file from an authenticated endpoint (window.open can't send the token)
export async function downloadFile(url) {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Download failed'); }
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
  const name = m ? decodeURIComponent(m[1] || m[2]) : 'download';
  const a = document.createElement('a');
  a.href = URL.createObjectURL(await res.blob()); a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

// Session expired / missing token → back to login, staying on the same URL
export function forceRelogin() {
  try { ['pf_auth', 'pf_client_id', 'pf_client_name', 'pf_username', 'pf_token'].forEach(k => localStorage.removeItem(k)); } catch (e) {}
  window.location.reload();
}

async function req(method, path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    if (res.status === 401 && err.error === 'auth_required') { forceRelogin(); throw new Error('سجّل دخول تاني'); }
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

export const api = {
  // Devices
  getDevices: (search, client_id) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (client_id) params.set('client_id', client_id);
    const q = params.toString();
    return req('GET', `/devices${q ? '?' + q : ''}`);
  },
  getDevice: (id) => req('GET', `/devices/${id}`),
  createDevice: (data) => req('POST', '/devices', data),
  updateDevice: (id, data) => req('PUT', `/devices/${id}`, data),
  deleteDevice: (id) => req('DELETE', `/devices/${id}`),

  // Tickets
  getTickets: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return req('GET', `/tickets${q ? `?${q}` : ''}`);
  },
  getTicket: (id) => req('GET', `/tickets/${id}`),
  createTicket: (data) => req('POST', '/tickets', data),
  updateTicket: (id, data) => req('PUT', `/tickets/${id}`, data),
  deleteTicket: (id) => req('DELETE', `/tickets/${id}`),

  // Parts
  getParts: (ticket_id) => req('GET', `/parts/ticket/${ticket_id}`),
  createPart: (data) => req('POST', '/parts', data),
  updatePart: (id, data) => req('PUT', `/parts/${id}`, data),
  deletePart: (id) => req('DELETE', `/parts/${id}`),

  // Invoices
  getInvoices: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return req('GET', `/invoices${q ? `?${q}` : ''}`);
  },
  getTicketInvoices: (ticket_id) => req('GET', `/invoices/ticket/${ticket_id}`),
  createInvoice: (data) => req('POST', '/invoices', data),
  updateInvoice: (id, data) => req('PUT', `/invoices/${id}`, data),
  deleteInvoice: (id) => req('DELETE', `/invoices/${id}`),

  // Invoice Parts linking
  getInvoiceParts: (invoice_id) => req('GET', `/invoices/${invoice_id}/parts`),
  linkPartToInvoice: (invoice_id, part_id) => req('POST', `/invoices/${invoice_id}/parts`, { part_id }),
  unlinkPartFromInvoice: (invoice_id, part_id) => req('DELETE', `/invoices/${invoice_id}/parts/${part_id}`),

  // Clients
  getClients: (params) => req('GET', `/clients${params ? `?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)))}` : ''}`),
  getClient: (id) => req('GET', `/clients/${id}`),
  getClientFacets: () => req('GET', '/clients/facets'),
  createClient: (data) => req('POST', '/clients', data),
  updateClient: (id, data) => req('PUT', `/clients/${id}`, data),
  deleteClient: (id) => req('DELETE', `/clients/${id}`),

  // Opportunities
  getOpportunities: () => req('GET', '/opportunities'),
  getOpportunity: (id) => req('GET', `/opportunities/${id}`),
  createOpportunity: (data) => req('POST', '/opportunities', data),
  updateOpportunity: (id, data) => req('PUT', `/opportunities/${id}`, data),
  deleteOpportunity: (id) => req('DELETE', `/opportunities/${id}`),
  addOpportunityNote: (id, data) => req('POST', `/opportunities/${id}/notes`, data),
  deleteOpportunityNote: (id, noteId) => req('DELETE', `/opportunities/${id}/notes/${noteId}`),
  // Part needs
  getPartNeeds: () => req('GET', '/part-needs'),
  createPartNeed: (data) => req('POST', '/part-needs', data),
  updatePartNeed: (id, data) => req('PUT', `/part-needs/${id}`, data),
  deletePartNeed: (id) => req('DELETE', `/part-needs/${id}`),
  // Tasks / calendar
  getCalendar: (from, to) => req('GET', `/tasks/calendar?from=${from}&to=${to}`),
  // Preventive maintenance
  pmPlans: () => req('GET', '/pm/plans'),
  pmCreatePlan: (d) => req('POST', '/pm/plans', d),
  pmUpdatePlan: (id, d) => req('PUT', `/pm/plans/${id}`, d),
  pmDeletePlan: (id) => req('DELETE', `/pm/plans/${id}`),
  pmLogVisit: (planId, d) => req('POST', `/pm/plans/${planId}/visits`, d),
  pmDeleteVisit: (id) => req('DELETE', `/pm/visits/${id}`),
  pmScan: (token) => req('GET', `/pm/scan/${encodeURIComponent(token)}`),
  pmLabels: () => req('GET', '/pm/labels'),
  portalMe: () => req('GET', '/portal/me'),
  portalDevices: () => req('GET', '/portal/devices'),
  portalDevice: (id) => req('GET', `/portal/devices/${id}`),
  portalTickets: () => req('GET', '/portal/tickets'),
  portalTicket: (id) => req('GET', `/portal/tickets/${id}`),
  portalMaintenance: () => req('GET', '/portal/maintenance'),
  portalVisit: (id) => req('GET', `/portal/maintenance/visits/${id}`),
  pmContracts: () => req('GET', '/pm/contracts'),
  pmContract: (id) => req('GET', `/pm/contracts/${id}`),
  pmCreateContract: (d) => req('POST', '/pm/contracts', d),
  pmUpdateContract: (id, d) => req('PUT', `/pm/contracts/${id}`, d),
  pmDeleteContract: (id) => req('DELETE', `/pm/contracts/${id}`),
  pmDuplicateContract: (id, d) => req('POST', `/pm/contracts/${id}/duplicate`, d),
  pmAddContractDevice: (id, d) => req('POST', `/pm/contracts/${id}/devices`, d),
  pmRemoveContractDevice: (cdId) => req('DELETE', `/pm/contract-devices/${cdId}`),
  pmSetTasks: (cdId, tasks) => req('PUT', `/pm/contract-devices/${cdId}/tasks`, { tasks }),
  pmApplyTasksToAll: (cdId) => req('POST', `/pm/contract-devices/${cdId}/apply-tasks-to-all`),
  pmVisit: (id) => req('GET', `/pm/visits/${id}`),
  pmUpdateVisit: (id, d) => req('PUT', `/pm/visits/${id}`, d),
  pmLogItem: (visitId, d) => req('POST', `/pm/visits/${visitId}/items`, d),
  pmDeleteItem: (id) => req('DELETE', `/pm/visit-items/${id}`),
  pmAddRepair: (visitId, d) => req('POST', `/pm/visits/${visitId}/repairs`, d),
  pmUpdateRepair: (ticketId, d) => req('PUT', `/pm/repairs/${ticketId}`, d),
  pmAddRepairPart: (ticketId, d) => req('POST', `/pm/repairs/${ticketId}/parts`, d),
  pmDeleteRepairPart: (partId) => req('DELETE', `/pm/repair-parts/${partId}`),
  pmAddVisitInvoice: (visitId, d) => req('POST', `/pm/visits/${visitId}/invoices`, d),
  pmDeleteContractFile: (id) => req('DELETE', `/pm/contracts/${id}/file`),
  async pmUploadContractFile(id, file) {
    const res = await fetch(`${BASE}/pm/contracts/${id}/file`, {
      method: 'POST',
      headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Filename': encodeURIComponent(file.name), ...authHeaders() },
      body: file,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); if (res.status === 401) forceRelogin(); throw new Error(e.error || 'Upload failed'); }
    return res.json();
  },
  async pmOpenContractFile(id) {
    const w = window.open('', '_blank');
    const res = await fetch(`${BASE}/pm/contracts/${id}/file`, { headers: authHeaders() });
    if (!res.ok) { if (w) w.close(); throw new Error('الملف مش موجود'); }
    const url = URL.createObjectURL(await res.blob());
    if (w) w.location.href = url; else window.location.href = url;
  },
  getTasks: () => req('GET', '/tasks'),
  createTask: (data) => req('POST', '/tasks', data),
  updateTask: (id, data) => req('PUT', `/tasks/${id}`, data),
  deleteTask: (id) => req('DELETE', `/tasks/${id}`),
  // TickTick
  ticktickStatus: () => req('GET', '/ticktick/status'),
  ticktickProjects: () => req('GET', '/ticktick/projects'),
  ticktickSetProject: (p) => req('POST', '/ticktick/project', p),
  ticktickSetPartsProject: (p) => req('POST', '/ticktick/parts-project', p),
  ticktickSync: () => req('POST', '/ticktick/sync'),
  ticktickDisconnect: () => req('POST', '/ticktick/disconnect'),
  getUsers: () => req('GET', '/users'),

  // Stats
  getStats: () => req('GET', '/stats'),
};
